import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/* ============================================================================
 *  Cookiebot se načítá STATICKY v index.html jako úplně první skript,
 *  včetně gtag("consent", "default", ...). Tenhle modul už Cookiebot
 *  NEVKLÁDÁ – jen čte jeho stav, překládá ho do Google consent mode
 *  a řídí podle něj Sklik.
 * ========================================================================== */

const GA_ID = "G-6QGZCZYMVG";

const SKLIK_RTG_ID = 86687;
const SKLIK_PURCHASE_CONVERSION_ID = 100284823;
const SKLIK_VISIT_CONVERSION_ID = 100284824;

const CONSENT_EVENTS = [
    "CookiebotOnConsentReady",
    "CookiebotOnAccept",
    "CookiebotOnDecline",
];

//#region Consent API ---------------------------------------------------------

/**
 * Aktuální stav souhlasu, nebo null dokud uživatel neodpověděl na banner.
 */
export function getConsent() {
    const cb = window.Cookiebot;

    if (!cb?.hasResponse || !cb.consent) {
        return null;
    }

    return {
        necessary: true,
        preferences: !!cb.consent.preferences,
        statistics: !!cb.consent.statistics,
        marketing: !!cb.consent.marketing,
    };
}

/**
 * Zavolá callback, jakmile je souhlas známý, a znovu při každé jeho ZMĚNĚ.
 * Pokud je souhlas známý už v okamžiku registrace, callback proběhne
 * synchronně. Vrací funkci pro odhlášení.
 *
 *   onConsent((consent) => {
 *       if (!consent.statistics) return;
 *       // ...inicializace měřicích věcí
 *   });
 */
export function onConsent(callback) {
    let lastKey = null;

    const run = () => {
        const consent = getConsent();

        if (!consent) return;

        // stejný stav podruhé (ConsentReady + Accept přijdou oba) -> neopakovat
        const key = [
            consent.preferences,
            consent.statistics,
            consent.marketing,
        ].join("|");

        if (key === lastKey) return;

        lastKey = key;

        try {
            callback(consent);
        } catch (e) {
            // měření nesmí shodit stránku
            console.error("onConsent callback failed", e);
        }
    };

    run();

    CONSENT_EVENTS.forEach((e) => window.addEventListener(e, run));

    return () => {
        CONSENT_EVENTS.forEach((e) => window.removeEventListener(e, run));
    };
}

/**
 * Jednorázový callback čekající na MARKETINGOVÝ souhlas.
 * Když uživatel odmítne a později souhlas změní, callback proběhne až tehdy.
 */
function whenMarketingConsent(cb) {
    let done = false;
    let off = null;

    const handler = (consent) => {
        if (done || !consent.marketing) return;

        done = true;
        cb();

        if (off) off();
    };

    off = onConsent(handler);

    // pokud handler proběhl synchronně, off ještě neexistovalo
    if (done) off();

    return off;
}

//#region Google consent mode + gtag ------------------------------------------

function applyGoogleConsent(consent) {
    const marketing = consent.marketing ? "granted" : "denied";
    const preferences = consent.preferences ? "granted" : "denied";

    window.gtag?.("consent", "update", {
        ad_storage: marketing,
        ad_user_data: marketing,
        ad_personalization: marketing,
        analytics_storage: consent.statistics ? "granted" : "denied",
        functionality_storage: preferences,
        personalization_storage: preferences,
    });
}

let gaLoaded = false;

function loadGoogleAnalytics() {
    if (gaLoaded) return;
    gaLoaded = true;

    // window.dataLayer, window.gtag, consent default i gtag("js") už
    // existují z index.html – tady jen doplníme config a stáhneme gtag.js
    window.gtag("config", GA_ID, {
        cookie_domain: "carskaut.com",
    });

    const script = document.createElement("script");

    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.setAttribute("data-cookieconsent", "ignore");

    document.head.appendChild(script);
}

let bridgeReady = false;

function initConsentBridge() {
    if (bridgeReady || typeof window === "undefined") return;
    bridgeReady = true;

    // 1) Cookiebot -> consent mode, hned jak je souhlas známý i při každé změně
    onConsent(applyGoogleConsent);

    // 2) gtag.js může jít nahoru rovnou – v advanced consent mode
    //    při denied stavu žádné cookies nezapisuje
    loadGoogleAnalytics();
}

initConsentBridge();

//#region Sklik ---------------------------------------------------------------

let sklikPromise = null;

/**
 * rc.js se smí načíst JEDINĚ po marketingovém souhlasu – volá se výhradně
 * zevnitř whenMarketingConsent(). Žádné data-cookieconsent="ignore".
 */
function loadSklik() {
    if (sklikPromise) return sklikPromise;

    sklikPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");

        script.async = true;
        script.src = "https://c.seznam.cz/js/rc.js";

        script.onload = resolve;
        script.onerror = reject;

        document.head.appendChild(script);
    });

    return sklikPromise;
}

function sklikHit(fn) {
    loadSklik()
        .then(() => {
            window.sznIVA?.IS?.updateIdentities({ eid: null });
            fn();
        })
        .catch(() => {
            // měření nesmí shodit stránku
        });
}

/**
 * Konverze Skliku (návštěva stránky / nákup).
 * dedupeKey chrání proti refreshi a proti dvojímu spuštění efektu.
 */
export function sklikConversionHit({ purchase = false, value, dedupeKey }) {
    const id = purchase
        ? SKLIK_PURCHASE_CONVERSION_ID
        : SKLIK_VISIT_CONVERSION_ID;

    return whenMarketingConsent(() => {
        if (dedupeKey) {
            const storageKey = `sklik_conv_${id}_${dedupeKey}`;

            try {
                if (window.sessionStorage.getItem(storageKey)) {
                    return;
                }

                window.sessionStorage.setItem(storageKey, "1");
            } catch (e) {
                // private mode, jedeme dál
            }
        }

        sklikHit(() => {
            const hit = { id, consent: 1 };
            if (value != null) hit.value = Math.round(value);
            window.rc?.conversionHit(hit);
        });
    });
}

function sklikRetargetingHit() {
    return whenMarketingConsent(() => {
        sklikHit(() => {
            window.rc?.retargetingHit({
                rtgId: SKLIK_RTG_ID,
                consent: 1,
            });
        });
    });
}

//#region Hook ----------------------------------------------------------------

// modulová paměť, aby StrictMode (dvojí spuštění efektu) neposlal hit dvakrát
// a aby čekání na souhlas přežilo remount komponenty
const sklikHitPaths = new Set();

export function useCookiebot(lng) {
    const { pathname } = useLocation();

    // Cookiebot si jazyk bere z <html lang> při načtení. Po přepnutí jazyka
    // v SPA zůstane banner v původním jazyce – to je v pořádku, uživatel na
    // něj v tu chvíli už odpověděl. Atribut držíme aktuální kvůli i18n a a11y.
    useEffect(() => {
        if (!lng || lng === "xx") return;

        if (document.documentElement.lang !== lng) {
            document.documentElement.lang = lng;
        }
    }, [lng]);

    // retargeting Skliku jednou na každou navštívenou cestu
    useEffect(() => {
        if (sklikHitPaths.has(pathname)) return;

        sklikHitPaths.add(pathname);

        sklikRetargetingHit();
    }, [pathname]);
}